
import { LeadContact } from "@/services/leadContactsService";
import { Table, TableBody, TableHead, TableHeader, TableRow, TableCell } from "@/components/ui/table";
import ContactRow from "../leads/contact/ContactRow";
import { Skeleton } from "@/components/ui/skeleton";
import { FileText, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";

interface ContactsTableProps {
  contacts: LeadContact[];
  onContactUpdated: () => void;
  onEditContact?: (contact: LeadContact) => void;
  onViewContact?: (contact: LeadContact) => void;
  onDeleteContact?: (contact: LeadContact) => void;
  onInitiateCall?: (contact: LeadContact) => void;
  isLoading?: boolean;
}

const ContactsTable = ({ 
  contacts, 
  onContactUpdated, 
  onEditContact,
  onViewContact,
  onDeleteContact,
  onInitiateCall,
  isLoading = false
}: ContactsTableProps) => {
  // Show loading state while contacts are being loaded or if no contacts but still loading
  if (isLoading || (contacts.length === 0 && isLoading)) {
    return (
      <div className="rounded-md border overflow-hidden w-full">
        <Table className="table-fixed">
          <TableHeader>
            <TableRow>
              <TableHead className="w-[50%] min-w-[120px] md:w-[40%] lg:w-[35%]">Name</TableHead>
              <TableHead className="hidden md:table-cell md:w-[25%] lg:w-[25%]">Company</TableHead>
              <TableHead className="w-[38%] md:w-[23%] lg:w-[28%]">Contact Info</TableHead>
              <TableHead className="w-[12%]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {[1, 2, 3, 4, 5].map((i) => (
              <TableRow key={i}>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Skeleton className="h-10 w-10 rounded-full flex-shrink-0" />
                    <div className="space-y-1 min-w-0">
                      <Skeleton className="h-4 w-24 md:w-32" />
                      <Skeleton className="h-3 w-16 md:w-20" />
                    </div>
                  </div>
                </TableCell>
                <TableCell className="hidden md:table-cell">
                  <Skeleton className="h-4 w-24" />
                </TableCell>
                <TableCell>
                  <div className="space-y-1">
                    <Skeleton className="h-3 w-full max-w-[180px]" />
                    <Skeleton className="h-3 w-20 md:w-28" />
                  </div>
                </TableCell>
                <TableCell>
                  <Skeleton className="h-8 w-8 rounded" />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    );
  }

  // Only show "No contacts found" if definitely not loading and no contacts
  if (contacts.length === 0 && !isLoading) {
    return (
      <div className="text-center py-16 border-2 border-dashed rounded-lg">
        <div className="mx-auto w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
          <FileText className="h-8 w-8 text-muted-foreground" />
        </div>
        <h3 className="text-lg font-medium mb-2">No contacts found</h3>
        <p className="text-muted-foreground mb-4">
          Try adjusting your search filters to find what you're looking for, or add your first contact.
        </p>
        <Button className="flex items-center mx-auto">
          <Plus size={16} className="mr-2" />
          Add Contact
        </Button>
      </div>
    );
  }

  return (
    <div className="rounded-md border overflow-hidden w-full">
      <Table className="table-fixed">
          <TableHeader>
            <TableRow>
              <TableHead className="w-[50%] min-w-[120px] md:w-[40%] lg:w-[35%]">Name</TableHead>
              <TableHead className="hidden md:table-cell md:w-[25%] lg:w-[25%]">Company</TableHead>
              <TableHead className="w-[38%] md:w-[23%] lg:w-[28%]">Contact Info</TableHead>
              <TableHead className="w-[12%]">Actions</TableHead>
            </TableRow>
          </TableHeader>
        <TableBody>
          {contacts.map((contact) => (
            <ContactRow 
              key={contact.id} 
              contact={contact} 
              onEdit={onEditContact ? () => onEditContact(contact) : () => {}}
              onView={onViewContact ? () => onViewContact(contact) : () => {}}
              onDelete={onDeleteContact ? () => onDeleteContact(contact) : () => {}}
              onOutreach={onInitiateCall ? () => onInitiateCall(contact) : () => {}}
            />
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export default ContactsTable;
